import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { toast } from "react-toastify";
import { jwtDecode } from "jwt-decode";
import AxiosInstance from "../utils/axiosInstance";
import { getItem } from "../utils/storage";
import {
  attachSystemColorSchemeListener,
  normalizeAdminThemePreference,
  resolveAdminThemeDark,
} from "../utils/adminThemePreference";

const AdminDarkModeContext = createContext({
  darkMode: false,
  adminThemePreference: "auto",
  setDarkMode: () => {},
  setAdminThemePreference: () => {},
  toggleDarkMode: () => {},
});

/** Notifies other tabs to refetch Super Admin theme from the API (localStorage is not the source of truth). */
const SUPERADMIN_DM_SYNC_KEY = "cj_superadmin_dm_sync";

function getAdminSessionToken() {
  return getItem("adminToken") || getItem("workerToken") || null;
}

/** Super Admin JWT: Role "Admin" + AdminId, no UserId (Company/Worker use UserId). */
function isSuperAdminPortalToken(token) {
  try {
    const d = jwtDecode(token);
    return d?.Role === "Admin" && Boolean(d?.AdminId) && !d?.UserId;
  } catch {
    return false;
  }
}

function persistCompanyWorkerTheme(pref, resolvedIsDark) {
  return AxiosInstance.patch("/v1/user/admin-dark-mode", {
    AdminThemePreference: pref,
    IsDarkMode: resolvedIsDark,
  });
}

function persistSuperAdminTheme(pref, resolvedIsDark) {
  return AxiosInstance.put("/v1/super-admin/dark-mode", {
    AdminThemePreference: pref,
    IsDarkMode: resolvedIsDark,
  });
}

/**
 * @param {"api" | "localStorage" | "superadmin"} persistence
 *   - `api`: Company / Worker — load from token_data; save PATCH /v1/user/admin-dark-mode.
 *   - `localStorage`: browser-only (no API).
 *   - `superadmin`: load GET /v1/super-admin/profile; save PUT /v1/super-admin/dark-mode.
 */
export function AdminDarkModeProvider({
  children,
  persistence = "api",
  localStorageKey = "cj_superadmin_dark_mode",
}) {
  const useLocalOnly = persistence === "localStorage";
  const useSuperadminApi = persistence === "superadmin";

  const [themePreference, setThemePreferenceState] = useState("auto");
  const [systemPrefersDark, setSystemPrefersDark] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.matchMedia("(prefers-color-scheme: dark)").matches;
  });

  const themePreferenceRef = useRef(themePreference);
  themePreferenceRef.current = themePreference;

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    return attachSystemColorSchemeListener(setSystemPrefersDark);
  }, []);

  const darkMode = useMemo(
    () => resolveAdminThemeDark(themePreference, systemPrefersDark),
    [themePreference, systemPrefersDark]
  );

  useEffect(() => {
    if (useLocalOnly && typeof window !== "undefined") {
      try {
        const onlyDark = localStorage.getItem(localStorageKey) === "1";
        setThemePreferenceState(onlyDark ? "dark" : "light");
      } catch {
        setThemePreferenceState("auto");
      }
    }
  }, [useLocalOnly, localStorageKey]);

  useEffect(() => {
    if (useLocalOnly) return undefined;
    let cancelled = false;

    if (useSuperadminApi) {
      let lastVisibilityRefetch = 0;

      const loadFromServer = async () => {
        if (cancelled) return;
        const token = getItem("adminToken");
        if (!token || !isSuperAdminPortalToken(token)) return;
        try {
          const res = await AxiosInstance.get("/v1/super-admin/profile");
          const ok =
            res?.data?.statusCode === 200 || res?.data?.statusCode === "200";
          if (!ok || cancelled) return;
          const d = res?.data?.data;
          if (!d) return;

          if (typeof window !== "undefined") {
            try {
              if (
                localStorage.getItem(localStorageKey) === "1" &&
                d.AdminThemePreference !== "dark" &&
                d.IsDarkMode !== true
              ) {
                await persistSuperAdminTheme("dark", true);
                if (!cancelled) {
                  setThemePreferenceState("dark");
                }
                localStorage.removeItem(localStorageKey);
                try {
                  localStorage.setItem(
                    SUPERADMIN_DM_SYNC_KEY,
                    String(Date.now())
                  );
                } catch {
                  /* ignore */
                }
                return;
              }
            } catch {
              /* fall through */
            }
          }

          const pref = normalizeAdminThemePreference(
            d.IsDarkMode === true,
            d.AdminThemePreference
          );
          if (!cancelled) {
            setThemePreferenceState(pref);
          }
        } catch {
          /* ignore */
        }
      };

      loadFromServer();
      const retryTimer = window.setTimeout(() => loadFromServer(), 500);

      const onStorageSync = (e) => {
        if (e.key !== SUPERADMIN_DM_SYNC_KEY || cancelled) return;
        loadFromServer();
      };
      window.addEventListener("storage", onStorageSync);

      const onBecameVisible = () => {
        if (cancelled || document.visibilityState !== "visible") return;
        const now = Date.now();
        if (now - lastVisibilityRefetch < 2500) return;
        lastVisibilityRefetch = now;
        loadFromServer();
      };
      document.addEventListener("visibilitychange", onBecameVisible);
      window.addEventListener("focus", onBecameVisible);

      return () => {
        cancelled = true;
        window.clearTimeout(retryTimer);
        window.removeEventListener("storage", onStorageSync);
        document.removeEventListener("visibilitychange", onBecameVisible);
        window.removeEventListener("focus", onBecameVisible);
      };
    }

    const load = async () => {
      const token = getAdminSessionToken();
      if (!token) return;
      try {
        const res = await AxiosInstance.post("/v1/auth/token_data", { token });
        const ok =
          res?.data?.statusCode === "200" || res?.data?.statusCode === 200;
        if (!ok || cancelled) return;
        const d = res?.data?.data;
        if (!d) return;
        const role = d?.Role;
        const canSyncDarkPref = role === "Company" || role === "Worker";
        if (!canSyncDarkPref) return;
        const pref = normalizeAdminThemePreference(
          d.IsDarkMode === true,
          d.AdminThemePreference
        );
        if (!cancelled) {
          setThemePreferenceState(pref);
        }
      } catch {
        /* ignore */
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [useLocalOnly, useSuperadminApi, localStorageKey]);

  const setAdminThemePreference = useCallback(
    (nextPref) => {
      if (nextPref !== "auto" && nextPref !== "light" && nextPref !== "dark") {
        return;
      }
      const prevPref = themePreferenceRef.current;
      if (nextPref === prevPref) return;

      const systemDark = window.matchMedia("(prefers-color-scheme: dark)")
        .matches;
      const resolved = resolveAdminThemeDark(nextPref, systemDark);

      setThemePreferenceState(nextPref);

      if (useLocalOnly) {
        try {
          localStorage.setItem(localStorageKey, resolved ? "1" : "0");
        } catch {
          /* ignore */
        }
        return;
      }

      if (useSuperadminApi) {
        persistSuperAdminTheme(nextPref, resolved)
          .then(() => {
            try {
              localStorage.setItem(
                SUPERADMIN_DM_SYNC_KEY,
                String(Date.now())
              );
            } catch {
              /* ignore */
            }
          })
          .catch((err) => {
            const msg =
              err?.response?.data?.message ||
              err?.response?.data?.error ||
              "Could not save theme preference.";
            toast.error(msg);
            setThemePreferenceState(prevPref);
          });
        return;
      }

      persistCompanyWorkerTheme(nextPref, resolved).catch(() => {
          toast.error("Could not save theme preference.");
          setThemePreferenceState(prevPref);
        });
    },
    [useLocalOnly, useSuperadminApi, localStorageKey]
  );

  const setDarkMode = useCallback(
    (value) => {
      const sys =
        typeof window !== "undefined" &&
        window.matchMedia("(prefers-color-scheme: dark)").matches;
      const currentDark = resolveAdminThemeDark(themePreferenceRef.current, sys);
      const nextResolved =
        typeof value === "function" ? Boolean(value(currentDark)) : Boolean(value);
      setAdminThemePreference(nextResolved ? "dark" : "light");
    },
    [setAdminThemePreference]
  );

  const toggleDarkMode = useCallback(() => {
    setAdminThemePreference(darkMode ? "light" : "dark");
  }, [darkMode, setAdminThemePreference]);

  const value = useMemo(
    () => ({
      darkMode,
      adminThemePreference: themePreference,
      setDarkMode,
      setAdminThemePreference,
      toggleDarkMode,
    }),
    [darkMode, themePreference, setDarkMode, setAdminThemePreference, toggleDarkMode]
  );

  return (
    <AdminDarkModeContext.Provider value={value}>
      {children}
    </AdminDarkModeContext.Provider>
  );
}

export function useAdminDarkMode() {
  return useContext(AdminDarkModeContext);
}
